import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Mail, Globe, Lock } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { EmailBuilder } from '@/components/email/EmailBuilder';
import { TemplateGallery } from '@/components/email/TemplateGallery';
import { type EmailBlock, type GalleryTemplate, createBlock } from '@/lib/emailBlocks';

// ── Super Admin: System Email Templates ─────────────────────────────────
//
// Reuses the same block-based EmailBuilder + TemplateGallery that the
// vendor side uses, so the authoring experience is identical. The only
// difference is where data lands — saApi hits /super-admin/email-templates
// which writes to the SystemEmailTemplate table (no tenantId).
//
// Vendors see these rows automatically via EmailBusinessService.getTemplates();
// editing one on the vendor side triggers a copy-on-write clone.

interface SystemTemplate {
  id: string;
  name: string;
  subject: string;
  htmlBody: string;
  blocks: EmailBlock[];
  category?: string;
  isActive: boolean;
  // When true this row is exposed to every vendor's /email-templates
  // page as a Default (clonable, read-only). When false it stays here
  // in the super-admin console — internal use only.
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export function SuperAdminEmailTemplatesPage() {
  const [templates, setTemplates] = useState<SystemTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<SystemTemplate | null>(null);
  const [showGallery, setShowGallery] = useState(false);
  const [builderState, setBuilderState] = useState<{
    isOpen: boolean;
    editId?: string;
    blocks: EmailBlock[];
    name: string;
    subject: string;
  } | null>(null);

  const load = () => {
    setLoading(true);
    saApi
      .get('/super-admin/email-templates')
      .then(({ data }) => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await saApi.delete(`/super-admin/email-templates/${confirmDelete.id}`);
      toast('success', 'System template deleted');
      setConfirmDelete(null);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete');
    }
  };

  // ── Builder open/save helpers — mirror EmailTemplatesPage ──────────────

  const openBuilder = (blocks: EmailBlock[], name = '', subject = '', editId?: string) => {
    setBuilderState({ isOpen: true, editId, blocks, name, subject });
    setShowGallery(false);
  };

  const handleGallerySelect = (t: GalleryTemplate) => {
    openBuilder(
      t.blocks.map((b) => ({ ...b, id: `b_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` })),
      t.name,
      t.subject,
    );
  };

  const handleBlankTemplate = () => {
    openBuilder(
      [createBlock('header'), createBlock('text'), createBlock('button'), createBlock('footer')],
      '',
      '',
    );
  };

  const handleSaveBuilder = async (data: { name: string; subject: string; blocks: EmailBlock[] }) => {
    try {
      const payload = {
        name: data.name,
        subject: data.subject,
        htmlBody: '',
        blocks: data.blocks,
        isActive: true,
      };
      if (builderState?.editId) {
        await saApi.patch(`/super-admin/email-templates/${builderState.editId}`, payload);
        toast('success', 'System template updated');
      } else {
        await saApi.post('/super-admin/email-templates', payload);
        toast('success', 'System template created');
      }
      setBuilderState(null);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save template');
    }
  };

  // Flip the row's isPublic flag inline — one click to publish to all
  // vendors or pull back to internal use. Optimistic UI keeps the flip
  // instant; reverts on failure so the badge doesn't lie.
  const togglePublic = async (t: SystemTemplate) => {
    const next = !t.isPublic;
    setTemplates((prev) => prev.map((r) => (r.id === t.id ? { ...r, isPublic: next } : r)));
    try {
      await saApi.patch(`/super-admin/email-templates/${t.id}`, { isPublic: next });
      toast('success', next ? 'Published to vendors' : 'Marked as internal');
    } catch (err: any) {
      // Revert optimistic flip
      setTemplates((prev) => prev.map((r) => (r.id === t.id ? { ...r, isPublic: t.isPublic } : r)));
      toast('error', err?.response?.data?.message || 'Failed to update visibility');
    }
  };

  const handleEditTemplate = (t: SystemTemplate) => {
    const blocks =
      Array.isArray(t.blocks) && t.blocks.length > 0
        ? t.blocks
        : [createBlock('header'), createBlock('text')];
    openBuilder(blocks, t.name, t.subject, t.id);
  };

  // ── Full-screen builder (same pattern as vendor EmailTemplatesPage) ─────

  if (builderState?.isOpen) {
    return (
      <EmailBuilder
        initialBlocks={builderState.blocks}
        initialName={builderState.name}
        initialSubject={builderState.subject}
        onSave={handleSaveBuilder}
        onBack={() => setBuilderState(null)}
      />
    );
  }

  return (
    <SuperAdminLayout title="Email Templates">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#6366F1]">System Email Templates</h2>
            <p className="text-sm text-[#64748B] mt-1">
              Platform-wide defaults. Every tenant sees these. When a tenant edits one, they get their own copy — your original stays untouched.
            </p>
          </div>
          <button
            onClick={() => setShowGallery(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg"
          >
            <Plus className="h-4 w-4" /> New Template
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-100 rounded-xl">
            <Mail className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-3">No system templates yet</p>
            <button
              onClick={() => setShowGallery(true)}
              className="px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg"
            >
              Create Your First Default
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => {
              const firstHeader = (Array.isArray(t.blocks) ? t.blocks : []).find(
                (b: any) => b.type === 'header',
              );
              const headerColor = firstHeader?.props?.bgColor || '#6366F1';
              return (
                <div
                  key={t.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group"
                >
                  <div
                    className="h-16 flex items-center justify-center px-4 relative"
                    style={{ backgroundColor: headerColor }}
                  >
                    <span className="text-white text-sm font-bold truncate">
                      {firstHeader?.props?.text || t.name}
                    </span>
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      {/* Visibility badge — visible to vendors OR internal only. */}
                      {t.isPublic ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-emerald-500/90 text-white px-1.5 py-0.5 rounded">
                          <Globe className="h-3 w-3" /> Public
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-slate-700/90 text-white px-1.5 py-0.5 rounded">
                          <Lock className="h-3 w-3" /> Internal
                        </span>
                      )}
                      {!t.isActive && (
                        <span className="text-[10px] font-semibold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-slate-800 truncate">{t.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{t.subject}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] text-slate-400">
                        {t.category || 'Platform default'}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {/* Publish / Unpublish toggle — flips isPublic in
                            place so super-admin can hand a template to
                            vendors without opening the builder. */}
                        <button
                          onClick={() => togglePublic(t)}
                          className={t.isPublic ? 'text-emerald-500 hover:text-emerald-700' : 'text-slate-400 hover:text-emerald-500'}
                          title={t.isPublic ? 'Currently visible to vendors — click to make internal' : 'Currently internal — click to publish to all vendors'}
                        >
                          {t.isPublic ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleEditTemplate(t)}
                          className="text-slate-400 hover:text-[#6366F1]"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(t)}
                          className="text-slate-400 hover:text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showGallery && (
        <TemplateGallery
          onSelect={handleGallerySelect}
          onBlank={handleBlankTemplate}
          onClose={() => setShowGallery(false)}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete system template?"
        message="Tenants that have already cloned this template will keep their copy. Tenants who haven't will stop seeing this default."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </SuperAdminLayout>
  );
}
